import { jsPDF } from 'jspdf';
import { isEmbeddedFontLoaded, registerEmbeddedFont } from '../pdf/embeddedFont';
import type { SifaTaetigkeit } from './betreuungTypes';
import { artLabel, formatDauer, sollIstVergleich, summeMinuten } from './taetigkeitenLogic';

/**
 * Taetigkeitsnachweis nach DGUV Vorschrift 2 als PDF (A4 hoch).
 *
 * Ein Jahr Betreuung auf Papier: Kopf mit Betrieb, Projekt und SiFa, dann
 * eine Tabelle Datum / Art / Taetigkeit / Dauer / Wer, Summenzeile, Soll/Ist
 * gegen die vereinbarte Einsatzzeit. Reine Funktion wie aushangPdf.ts:
 * Eingaben rein, Blob raus, kein `window`, kein `document`, kein
 * `doc.save()` — der Aufrufer haengt den Blob an einen Link oder eine Mail.
 *
 * Die Tabelle ist ein einfaches Zeilenlayout mit eigenem Seitenumbruch
 * (kein jspdf-autotable): jede Zeile misst ihre Hoehe an der umbrochenen
 * Titelspalte, und wer nicht mehr auf die Seite passt, beginnt eine neue
 * samt Tabellenkopf. Die Zeilenaufbereitung (`nachweisZeilen`) ist getrennt
 * und rein — sie wird getestet, das Zeichnen nicht.
 *
 * Schrift wie beim Zertifikat: eingebettete Liberation Sans, wenn der
 * Aufrufer sie vorher mit `ensureEmbeddedFont()` geladen hat; sonst
 * Helvetica (Umlaute lesbar, nur nicht PDF/A).
 */
export interface TaetigkeitsnachweisDaten {
  projektName: string;
  projektNummer: string;
  /** Der betreute Betrieb (Kunde), wie er im Kopf erscheinen soll. */
  betrieb: string;
  jahr: number;
  /** Die Taetigkeiten — andere Jahre werden herausgefiltert, nichts wird umgedeutet. */
  taetigkeiten: readonly SifaTaetigkeit[];
  /** Vereinbarte Einsatzzeit je Jahr in Stunden; null/0 = nicht hinterlegt. */
  sollStunden: number | null | undefined;
  /** Name der Fachkraft fuer Arbeitssicherheit (Unterschriftszeile). */
  sifaName: string;
  /** Betreuendes Buero fuer Kopf und Fusszeile. */
  company?: {
    name?: string | null;
    /** Anschrift, Zeilen durch Umbruch oder Komma getrennt. */
    adresse?: string | null;
    /** Logo als DataURL (PNG/JPEG); anderes wird stillschweigend uebersprungen. */
    logoDataUrl?: string | null;
  } | null;
}

/** Eine fertig formatierte Tabellenzeile. */
export interface NachweisZeile {
  /** dd.mm.yyyy */
  datum: string;
  art: string;
  titel: string;
  /** Beschreibung (klein unter dem Titel), leer wenn keine. */
  beschreibung: string;
  /** „1 h 30 min" */
  dauer: string;
  dauerMinuten: number;
  wer: string;
  /** Bezug (Berichtsnummer o. ae.), leer wenn keiner. */
  bezug: string;
}

export interface NachweisAufbereitung {
  zeilen: NachweisZeile[];
  summeMinuten: number;
  anzahl: number;
}

/** „2026-09-07" → „07.09.2026"; Unbekanntes bleibt wie es ist. */
export const datumDe = (iso: string | null | undefined): string => {
  const s = (iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

const s = (v: string | null | undefined): string => (v ?? '').toString().trim();

/**
 * Zeilen des Nachweises: nur das Jahr `jahr` (wenn angegeben), chronologisch
 * aufsteigend (gleiches Datum: Erfassungsreihenfolge), Datum und Dauer
 * formatiert, Art als Anzeigename. Summe und Anzahl aus GENAU diesen Zeilen —
 * die Summenzeile im PDF kann nicht von der Tabelle abweichen.
 */
export const nachweisZeilen = (taetigkeiten: readonly SifaTaetigkeit[], jahr?: number): NachweisAufbereitung => {
  const imJahr = typeof jahr === 'number'
    ? taetigkeiten.filter((t) => (t.datum || '').startsWith(`${jahr}-`))
    : [...taetigkeiten];
  const sortiert = [...imJahr].sort(
    (a, b) => (a.datum || '').localeCompare(b.datum || '') || (a.createdAt || '').localeCompare(b.createdAt || ''),
  );
  const zeilen = sortiert.map((t): NachweisZeile => ({
    datum: datumDe(t.datum),
    art: artLabel(t.art),
    titel: s(t.titel) || '—',
    beschreibung: s(t.beschreibung),
    dauer: formatDauer(t.dauerMinuten),
    dauerMinuten: Number.isFinite(t.dauerMinuten) && t.dauerMinuten > 0 ? t.dauerMinuten : 0,
    wer: s(t.durchgefuehrtVon),
    bezug: s(t.bezug),
  }));
  return { zeilen, summeMinuten: summeMinuten(sortiert), anzahl: zeilen.length };
};

// ─── Zeichnen ───────────────────────────────────────────────────────────────

const SEITE_B = 210;
const SEITE_H = 297;
const RAND = 18;
const BREITE = SEITE_B - 2 * RAND;
const FUSS_H = 12;

/** Spaltenbreiten in mm — Titel bekommt den Rest. */
const SP_DATUM = 22;
const SP_ART = 30;
const SP_DAUER = 20;
const SP_WER = 32;
const SP_TITEL = BREITE - SP_DATUM - SP_ART - SP_DAUER - SP_WER;

const ZEILE_MIN_H = 7;
const ZEILEN_ABSTAND = 4.2;

const bildFormat = (dataUrl: string | null | undefined): 'PNG' | 'JPEG' | undefined => {
  const v = (dataUrl || '').trim();
  if (/^data:image\/png;base64,/i.test(v)) return 'PNG';
  if (/^data:image\/jpe?g;base64,/i.test(v)) return 'JPEG';
  return undefined;
};

const adressZeilen = (adresse: string | null | undefined): string[] =>
  (adresse || '').split(/\r?\n|,/).map((t) => t.trim()).filter(Boolean);

export const erzeugeTaetigkeitsnachweisPdf = (daten: TaetigkeitsnachweisDaten): Blob => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  if (isEmbeddedFontLoaded()) registerEmbeddedFont(doc);

  const bueroName = s(daten.company?.name);
  const { zeilen, summeMinuten: summe, anzahl } = nachweisZeilen(daten.taetigkeiten, daten.jahr);
  const vergleich = sollIstVergleich(summe, daten.sollStunden);

  let y = RAND;

  // ── Kopf: Buero links, Logo rechts ────────────────────────────────────
  const logoFormat = bildFormat(daten.company?.logoDataUrl);
  if (logoFormat) {
    try {
      const props = doc.getImageProperties(daten.company?.logoDataUrl as string);
      const faktor = Math.min(45 / props.width, 16 / props.height);
      doc.addImage(daten.company?.logoDataUrl as string, logoFormat, SEITE_B - RAND - props.width * faktor, y - 3, props.width * faktor, props.height * faktor);
    } catch {
      // Ein unlesbares Logo darf den Nachweis nicht verhindern.
    }
  }
  if (bueroName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text(bueroName, RAND, y);
    y += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    for (const zeile of adressZeilen(daten.company?.adresse)) {
      doc.text(zeile, RAND, y);
      y += 3.8;
    }
  }
  y = Math.max(y, RAND + 14);
  doc.setDrawColor(13, 148, 136);
  doc.setLineWidth(0.6);
  doc.line(RAND, y, SEITE_B - RAND, y);
  y += 10;

  // ── Titel ─────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(15, 23, 42);
  doc.text(`Tätigkeitsnachweis ${daten.jahr}`, RAND, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(13, 148, 136);
  doc.text('Sicherheitstechnische Betreuung nach DGUV Vorschrift 2', RAND, y);
  y += 9;

  // ── Angaben ───────────────────────────────────────────────────────────
  const angaben: Array<[string, string]> = [
    ['Betrieb', s(daten.betrieb) || '—'],
    ['Projekt', [s(daten.projektNummer), s(daten.projektName)].filter(Boolean).join(' · ') || '—'],
    ['Fachkraft für Arbeitssicherheit', s(daten.sifaName) || '—'],
    ['Zeitraum', `01.01.${daten.jahr} – 31.12.${daten.jahr}`],
  ];
  doc.setFontSize(9.5);
  for (const [label, wert] of angaben) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(label, RAND, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    const wertZeilen = doc.splitTextToSize(wert, BREITE - 58) as string[];
    doc.text(wertZeilen, RAND + 56, y);
    y += wertZeilen.length * 4.6 + 1;
  }
  y += 5;

  // ── Tabelle ───────────────────────────────────────────────────────────
  const xDatum = RAND;
  const xArt = xDatum + SP_DATUM;
  const xTitel = xArt + SP_ART;
  const xDauer = xTitel + SP_TITEL; // rechtsbuendig am Spaltenende
  const xWer = xDauer + SP_DAUER;
  const unten = SEITE_H - RAND - FUSS_H;

  const tabellenKopf = () => {
    doc.setFillColor(241, 245, 249);
    doc.rect(RAND, y - 4.5, BREITE, 6.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(51, 65, 85);
    doc.text('Datum', xDatum + 1, y);
    doc.text('Art', xArt + 1, y);
    doc.text('Tätigkeit', xTitel + 1, y);
    doc.text('Dauer', xDauer + SP_DAUER - 1, y, { align: 'right' });
    doc.text('Wer', xWer + 1, y);
    y += 5;
  };

  const neueSeite = () => {
    doc.addPage();
    y = RAND;
    tabellenKopf();
  };

  tabellenKopf();
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);

  if (zeilen.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Für ${daten.jahr} sind keine Tätigkeiten erfasst.`, RAND + 1, y + 1);
    y += ZEILE_MIN_H;
  }

  for (const z of zeilen) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const titelZeilen = doc.splitTextToSize(z.titel, SP_TITEL - 2) as string[];
    doc.setFontSize(7.5);
    const beschreibungZeilen = z.beschreibung ? (doc.splitTextToSize(z.beschreibung, SP_TITEL - 2) as string[]) : [];
    const bezugZeile = z.bezug ? `Bezug: ${z.bezug}` : '';
    doc.setFontSize(9);
    const werZeilen = doc.splitTextToSize(z.wer || '—', SP_WER - 2) as string[];
    const artZeilen = doc.splitTextToSize(z.art, SP_ART - 2) as string[];
    const hoehe = Math.max(
      ZEILE_MIN_H,
      titelZeilen.length * ZEILEN_ABSTAND + (beschreibungZeilen.length + (bezugZeile ? 1 : 0)) * 3.4 + 2.5,
      werZeilen.length * ZEILEN_ABSTAND + 2.5,
      artZeilen.length * ZEILEN_ABSTAND + 2.5,
    );
    if (y + hoehe > unten) neueSeite();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    doc.text(z.datum, xDatum + 1, y);
    doc.text(artZeilen, xArt + 1, y);
    doc.text(titelZeilen, xTitel + 1, y);
    doc.text(z.dauer, xDauer + SP_DAUER - 1, y, { align: 'right' });
    doc.text(werZeilen, xWer + 1, y);
    let yKlein = y + titelZeilen.length * ZEILEN_ABSTAND - 0.6;
    if (beschreibungZeilen.length || bezugZeile) {
      doc.setFontSize(7.5);
      doc.setTextColor(100, 116, 139);
      if (beschreibungZeilen.length) {
        doc.text(beschreibungZeilen, xTitel + 1, yKlein);
        yKlein += beschreibungZeilen.length * 3.4;
      }
      if (bezugZeile) doc.text(bezugZeile, xTitel + 1, yKlein);
    }
    y += hoehe;
    doc.line(RAND, y - 4, SEITE_B - RAND, y - 4);
  }

  // ── Summenzeile ───────────────────────────────────────────────────────
  if (y + 8 > unten) neueSeite();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  doc.text(`Summe (${anzahl} ${anzahl === 1 ? 'Eintrag' : 'Einträge'})`, xTitel + 1, y);
  doc.text(formatDauer(summe), xDauer + SP_DAUER - 1, y, { align: 'right' });
  doc.setDrawColor(71, 85, 105);
  doc.setLineWidth(0.4);
  doc.line(RAND, y - 4.5, SEITE_B - RAND, y - 4.5);
  y += 10;

  // ── Soll / Ist ────────────────────────────────────────────────────────
  if (y + 24 > unten) neueSeite();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Einsatzzeit', RAND, y);
  y += 5.5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(51, 65, 85);
  const stunden = (h: number) => `${h.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} h`;
  if (vergleich.sollStunden > 0) {
    doc.text(`Soll (vereinbart): ${stunden(vergleich.sollStunden)}`, RAND, y);
    y += 4.8;
    doc.text(`Ist (erbracht): ${stunden(vergleich.istStunden)} — ${vergleich.prozent} % des Solls`, RAND, y);
    y += 4.8;
    doc.text(
      vergleich.restStunden > 0 ? `Noch offen: ${stunden(vergleich.restStunden)}` : 'Das vereinbarte Soll ist erreicht.',
      RAND,
      y,
    );
    y += 4.8;
  } else {
    doc.text(`Ist (erbracht): ${stunden(vergleich.istStunden)} — keine Soll-Einsatzzeit hinterlegt.`, RAND, y);
    y += 4.8;
  }
  y += 10;

  // ── Unterschrift ──────────────────────────────────────────────────────
  if (y + 18 > unten) neueSeite();
  y = Math.max(y, unten - 22);
  doc.setDrawColor(71, 85, 105);
  doc.setLineWidth(0.4);
  doc.line(RAND, y + 8, RAND + 70, y + 8);
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`Fachkraft für Arbeitssicherheit${s(daten.sifaName) ? ` · ${s(daten.sifaName)}` : ''}`, RAND, y + 12);
  doc.line(SEITE_B - RAND - 70, y + 8, SEITE_B - RAND, y + 8);
  doc.text('Betrieb (Kenntnisnahme)', SEITE_B - RAND - 70, y + 12);

  // ── Fusszeile auf jeder Seite ─────────────────────────────────────────
  const seiten = doc.getNumberOfPages();
  for (let i = 1; i <= seiten; i += 1) {
    doc.setPage(i);
    const fussY = SEITE_H - RAND;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(RAND, fussY - 4, SEITE_B - RAND, fussY - 4);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('Tätigkeitsnachweis nach DGUV Vorschrift 2', RAND, fussY);
    const rechts = [bueroName, `Seite ${i} von ${seiten}`].filter(Boolean).join(' · ');
    doc.text(rechts, SEITE_B - RAND, fussY, { align: 'right' });
  }

  return doc.output('blob');
};
